// src/services/appointmentApi.js
// Servicio para manejar operaciones relacionadas con citas

import { dotoniwoofApi } from '../api/dotoniwooApi';

/**
 * Crea una nueva cita
 * @param {Object} appointmentData - Datos de la cita
 * @returns {Promise<Object>} Respuesta de la API
 */
export const createAppointment = async (appointmentData) => {
  try {
    console.log('Enviando datos de la cita:', appointmentData);
    
    const response = await dotoniwoofApi.post('/appointment.php', appointmentData);
    
    console.log('Respuesta del servidor:', response.data);
    
    return {
      ok: true,
      status: response.status,
      data: response.data
    };
  } catch (error) {
    console.error('Error al crear cita:', error);
    
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * Obtiene las citas de un mes específico
 * @param {number} year - Año
 * @param {number} month - Mes (1-12)
 * @returns {Promise<Object>} Lista de citas del mes
 */
export const getAppointmentsByMonth = async (year, month) => {
  try {
    const response = await dotoniwoofApi.get(`/appointment.php?year=${year}&month=${month}`);
    
    return {
      ok: true,
      status: response.status,
      data: response.data
    };
  } catch (error) {
    console.error('Error al obtener citas:', error);
    
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * Obtiene las citas de un día específico
 * @param {string} date - Fecha en formato YYYY-MM-DD
 * @param {number} [employeeId] - Opcional: filtrar por id_employee (groomer)
 * @returns {Promise<Object>} Lista de citas del día
 */
export const getAppointmentsByDate = async (date, employeeId = null) => {
  try {
    const params = new URLSearchParams();
    params.append('from', date);
    params.append('to', date);
    params.append('limit', '50');
    params.append('offset', '0');
    if (employeeId != null) params.append('employee_id', String(employeeId));
    const response = await dotoniwoofApi.get(`/appointment.php?${params.toString()}`);

    return {
      ok: true,
      status: response.status,
      data: response.data
    };
  } catch (error) {
    console.error('Error al obtener citas del día:', error);

    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * Obtiene una cita por su ID
 * @param {number} appointmentId - ID de la cita
 * @returns {Promise<Object>} Datos de la cita
 */
export const getAppointmentById = async (appointmentId) => {
  try {
    const response = await dotoniwoofApi.get(`/appointment.php/${appointmentId}`);
    
    return {
      ok: true,
      status: response.status,
      data: response.data
    };
  } catch (error) {
    console.error('Error al obtener cita:', error);
    
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * Actualiza una cita existente
 * @param {number} appointmentId - ID de la cita
 * @param {Object} appointmentData - Nuevos datos de la cita
 * @returns {Promise<Object>} Respuesta de la API
 */
export const updateAppointment = async (appointmentId, appointmentData) => {
  try {
    console.log('Actualizando cita:', appointmentId, appointmentData);
    
    const response = await dotoniwoofApi.put(`/appointment.php/${appointmentId}`, appointmentData);
    
    console.log('Respuesta del servidor:', response.data);
    
    return {
      ok: true,
      status: response.status,
      data: response.data
    };
  } catch (error) {
    console.error('Error al actualizar cita:', error);
    
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * Elimina una cita
 * @param {number} appointmentId - ID de la cita
 * @returns {Promise<Object>} Respuesta de la API
 */
export const deleteAppointment = async (appointmentId) => {
  try {
    const response = await dotoniwoofApi.delete(`/appointment.php/${appointmentId}`);
    
    return {
      ok: true,
      status: response.status,
      data: response.data
    };
  } catch (error) {
    console.error('Error al eliminar cita:', error);
    
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * Obtiene los items de servicios de grooming
 * @returns {Promise<Object>} Lista de items de grooming
 */
export const getGroomingItems = async () => {
  try {
    const response = await dotoniwoofApi.get('/item.php?service_type=Gromming');
    
    return {
      ok: true,
      status: response.status,
      data: response.data
    };
  } catch (error) {
    console.error('Error al obtener items de grooming:', error);
    
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * Obtiene las citas con filtros múltiples
 * @param {Object} filters - Filtros de búsqueda
 * @param {number} filters.pet_id - ID de la mascota (opcional)
 * @param {number} filters.status_id - ID del estado (opcional)
 * @param {number} filters.item_id - ID del servicio (opcional)
 * @param {string} filters.from - Fecha inicio (YYYY-MM-DD)
 * @param {string} filters.to - Fecha fin (YYYY-MM-DD)
 * @param {number} filters.limit - Límite de resultados (por defecto 50)
 * @param {number} filters.offset - Offset para paginación (por defecto 0)
 * @returns {Promise<Object>} Lista de citas filtradas
 */
export const getAppointmentsWithFilters = async (filters = {}) => {
  try {
    const params = new URLSearchParams();

    if (filters.pet_id) params.append('pet_id', filters.pet_id);
    if (filters.client_id) params.append('client_id', filters.client_id);
    if (filters.employee_id != null) params.append('employee_id', String(filters.employee_id));
    if (filters.status_id) params.append('status_id', filters.status_id);
    if (filters.item_id) params.append('item_id', filters.item_id);
    if (filters.from) params.append('from', filters.from);
    if (filters.to) params.append('to', filters.to);

    params.append('limit', filters.limit || 50);
    params.append('offset', filters.offset || 0);

    const response = await dotoniwoofApi.get(`/appointment.php?${params.toString()}`);
    
    return {
      ok: true,
      status: response.status,
      data: response.data
    };
  } catch (error) {
    console.error('Error al obtener citas con filtros:', error);
    
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * Obtiene los estados para agenda de grooming
 * @returns {Promise<Object>} Lista de estados
 */
export const getAppointmentStatuses = async () => {
  try {
    const response = await dotoniwoofApi.get('status.php/agenda_gromming');
    
    return {
      ok: true,
      status: response.status,
      data: response.data
    };
  } catch (error) {
    console.error('Error al obtener estados de agenda:', error);
    
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * Obtiene citas de una mascota con detalle de servicios e informed_consent
 * GET /appointment.php?action=by-pet-with-services&pet_id=X
 * @param {number} petId - ID de la mascota
 * @returns {Promise<Object>} { data: array de citas con services e informed_consent }
 */
export const getAppointmentsByPetWithServices = async (petId) => {
  try {
    const response = await dotoniwoofApi.get(
      `/appointment.php?action=by-pet-with-services&pet_id=${petId}`
    );
    return {
      ok: true,
      status: response.status,
      data: response.data
    };
  } catch (error) {
    console.error('Error al obtener historial por mascota:', error);
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * Obtiene las últimas citas de un cliente y/o mascota
 * @param {number} clientId - ID del cliente
 * @param {number} [petId] - ID de la mascota (opcional)
 * @param {number} [limit=10] - Límite de citas a devolver
 * @returns {Promise<Object>} Lista de citas del cliente/mascota
 */
export const getLastAppointmentsByClient = async (clientId, petId = null, limit = 10) => {
  try {
    const params = new URLSearchParams();
    params.append('client_id', clientId.toString());
    if (petId) {
      params.append('pet_id', petId.toString());
    }
    if (limit) {
      params.append('limit', limit.toString());
    }
    
    const response = await dotoniwoofApi.get(`/appointment.php?${params.toString()}`);
    
    return {
      ok: true,
      status: response.status,
      data: response.data
    };
  } catch (error) {
    console.error('Error al obtener citas del cliente/mascota:', error);
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error al obtener citas del cliente/mascota' }
    };
  }
};

/**
 * Obtiene la venta asociada a una cita
 * GET appointment.php?action=sale-by-appointment&id=123
 * @param {number} appointmentId - ID de la cita
 * @returns {Promise<Object>} Datos de la venta asociada
 */
export const getSaleByAppointment = async (appointmentId) => {
  try {
    const response = await dotoniwoofApi.get(
      `/appointment.php?action=sale-by-appointment&id=${appointmentId}`
    );
    return {
      ok: true,
      status: response.status,
      data: response.data
    };
  } catch (error) {
    console.error('Error al obtener venta de la cita:', error);
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error al obtener venta de la cita' }
    };
  }
};

/**
 * Obtiene la última cita completada de cada cliente
 * @returns {Promise<Object>} Lista de últimas citas completadas por cliente
 */
export const getLastCompletedAppointmentsByClient = async () => {
  try {
    const response = await dotoniwoofApi.get('/appointment.php?action=last-completed-by-client');
    
    return {
      ok: true,
      status: response.status,
      data: response.data
    };
  } catch (error) {
    console.error('Error al obtener últimas citas completadas:', error);
    
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};
